import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  LinkOff as UnlinkIcon,
 
} from '@mui/icons-material';

import {
  Bug as BugIcon,
  BookOpen as EpicIcon,
  FileText as StoryIcon,
  CheckSquare as TaskIcon,
} from 'lucide-react';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HierarchicalParentMenu = ({ 
  issue,
  onParentChange,
  onClose
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableParents, setAvailableParents] = useState([]);
  const [currentParent, setCurrentParent] = useState(null);
  const [allowedParentTypes, setAllowedParentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);




  const issueTypes={
    1: {name:"Bug", icon: BugIcon, color: '#e53e3e'},
    2:{name:"Epic", icon: EpicIcon, color: '#805ad5'},
    3:{name:"Story", icon: StoryIcon, color: '#38a169'},
    4:{name:"Task", icon: TaskIcon, color: '#3182ce'},
  }

  useEffect(() => {
    fetchAvailableParents();
  }, [issue.id]);

  const fetchAvailableParents = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/issues/${issue.id}/available-parents?projectId=${issue.project}`,{
          headers:{
            Authorization: `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        setAvailableParents(data.data.availableParents || []);
        setCurrentParent(data.data.currentParent);
        setAllowedParentTypes(data.data.allowedParentTypes || []);
      } else {
        //console.error('Failed to fetch available parents:', data.message);
      }
    } catch (error) {
      //console.error('Error fetching available parents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleParentChange = async (newParent) => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setUpdating(true);
      const response = await fetch(`${API_URL}/api/issues/${issue.id}/parent`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          parentId: newParent?.id || null,
          parentType: newParent?.issueType || null
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setCurrentParent(newParent);
        if (onParentChange) {
          onParentChange(issue.id, newParent);
        }
      } else {
        //console.error('Failed to update parent:', data.message);
      }
    } catch (error) {
      //console.error('Error updating parent:', error);
    } finally {
      setUpdating(false);
      if (onClose) onClose();
    }
  };

  const handleUnlinkParent = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setUpdating(true);
      const response = await fetch(`${API_URL}/api/issues/${issue.id}/parent`, {
        method: 'DELETE'
     ,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setCurrentParent(null);
        if (onParentChange) {
          onParentChange(issue.id, null);
        }
      } else {
        //console.error('Failed to unlink parent:', data.message);
      }
    } catch (error) {
      //console.error('Error unlinking parent:', error);
    } finally {
      setUpdating(false);
      if (onClose) onClose();
    }
  };

  // Filter available issues based on search
  const getFilteredParentOptions = () => {
    if (!searchQuery) return availableParents;
    
    const query = searchQuery.toLowerCase();
    return availableParents.filter(item => 
      item.key.toLowerCase().includes(query) ||
      item.summary.toLowerCase().includes(query)
    );
  };

  const getTypeIcon = (typeId) => {
    const IconComponent = issueTypes[typeId]?.icon || EpicIcon;
    return <IconComponent sx={{ fontSize: '16px', color: issueTypes[typeId]?.color || 'text.secondary' }} />;
  };

  const filteredOptions = getFilteredParentOptions();

  if (loading) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading parent options...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: '320px',
        maxHeight: '400px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '3px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Typography variant="body2" fontWeight="600" color="text.primary">
          Set Parent for {issueTypes[issue.issueType]?.name || 'Issue'}
        </Typography>
      </Box>

      {/* Current Parent Info */}
      {currentParent && (
        <Box sx={{ padding: '8px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Current parent:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            {getTypeIcon(currentParent.issueType)}
            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem', fontWeight: 500 }}>
              {currentParent.key} - {currentParent.summary}
            </Typography>
          </Box>
        </Box>
      )}

      {/* No parent options for Epic */}
      {allowedParentTypes.length === 0 && (
        <Box sx={{ padding: '20px', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Epics cannot have parent issues
          </Typography>
        </Box>
      )}

      {/* Search box */}
      {allowedParentTypes.length > 0 && (
        <Box sx={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <TextField
            size="small"
            placeholder="Search issues..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Unlink option */}
      {allowedParentTypes.length > 0 && currentParent && (
        <>
          <Box
            onClick={handleUnlinkParent}
            sx={{
              padding: '12px 16px',
              cursor: updating ? 'not-allowed' : 'pointer',
              '&:hover': !updating ? { backgroundColor: '#f8f9fa' } : {},
              display: 'flex',
              alignItems: 'center',
              opacity: updating ? 0.6 : 1
            }}
          >
            <UnlinkIcon sx={{ fontSize: '16px', mr: 2, color: '#dc2626' }} />
            <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 500 }}>
              {updating ? 'Unlinking...' : 'Unlink parent'}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Available parent options */}
      {allowedParentTypes.length > 0 && (
        <>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((parentOption) => (
              <Box
                key={parentOption.id}
                onClick={() => !updating && handleParentChange(parentOption)}
                sx={{
                  padding: '12px 16px',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  '&:hover': !updating ? { backgroundColor: '#f8f9fa' } : {},
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #f0f0f0',
                  opacity: updating ? 0.6 : 1
                }}
              >
                {getTypeIcon(parentOption.issueType)}
                <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {parentOption.key}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem',
                      color: '#666',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {parentOption.summary}
                  </Typography>
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    color: issueTypes[parentOption.issueType]?.color || 'text.secondary',
                    fontWeight: 500,
                    ml: 1
                  }}
                >
                  {issueTypes[parentOption.issueType]?.name}
                </Typography>
              </Box>
            ))
          ) : (
            <Box sx={{ padding: '20px', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No matching issues found' : 'No available parent issues'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {updating && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1
        }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};

export default HierarchicalParentMenu;